/* eslint-disable no-param-reassign */
import axios from 'axios';
import NodeRSA from 'node-rsa';
import { PUBLIC_KEY_HEADER } from '@/utils/api';

const rsaKey = new NodeRSA(PUBLIC_KEY_HEADER);
const SENSITIVE_KEY = ['userId', 'role', 'subPassword', 'password'] as const;

const SESSION_KEYS = {
  auth: 'ambgames-user-authenticate',
  balance: 'ambgames-user-balance',
  refresh: 'ambgames-user-refresh-token',
  access: 'ambgames-user-access-token'
} as const;

const VUE_SESSION_KEY = 'vue-session-key';

type SessionBag = Record<string, any>;

function readSessionBag(): SessionBag {
  const raw = sessionStorage.getItem(VUE_SESSION_KEY);
  if (!raw) return {};

  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeSessionBag(bag: SessionBag) {
  if (!Object.keys(bag).length) {
    sessionStorage.removeItem(VUE_SESSION_KEY);
    return;
  }

  sessionStorage.setItem(VUE_SESSION_KEY, JSON.stringify(bag));
}

function sessionGet<T = any>(sessionKey: string): T | null {
  const bag = readSessionBag();

  if (!(sessionKey in bag)) return null;

  return bag[sessionKey] as T;
}

function sessionSet(sessionKey: string, value: any) {
  const bag = readSessionBag();
  bag[sessionKey] = value;
  writeSessionBag(bag);
}

function sessionRemove(sessionKey: string) {
  const bag = readSessionBag();

  if (sessionKey in bag) {
    delete bag[sessionKey];
    writeSessionBag(bag);
  }
}

function defaultUserPersistent() {
  return {
    user: {
      _id: null,
      username: null,
      type: null,
      nickname: null,
      source: null,
      level: null,
      wallet: null,
      commission: null,
      ipWhitelist: null,
      isFeature: null,
      isMechanic: null,
      showCurrencySigns: null
    },
    token: ''
  };
}

function dateTimeFormatter(unix: number | string | Date) {
  const t = new Date(unix);
  const zeroPad = (value: number | string, size: number): string =>
    String(value).length < size ? zeroPad(`0${String(value)}`, size) : String(value);

  return `${zeroPad(t.getFullYear(), 2)}-${zeroPad(
    t.getMonth() + 1,
    2
  )}-${zeroPad(t.getDate(), 2)} ${zeroPad(t.getHours(), 2)}:${zeroPad(
    t.getMinutes(),
    2
  )}:${zeroPad(t.getSeconds(), 2)}`;
}

function commaFormatter(n: number | string) {
  return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

function upperCamel(text: string) {
  return text.replace(/(?:^\w|[A-Z]|\b\w|\s+)/g, (match, index) => {
    if (+match === 0) return '';
    return index === 0 ? match.toUpperCase() : match.toLowerCase();
  });
}

const utils = {
  dateTimeFormatter,
  commaFormatter,
  upperCamel,

  getUserPersistent() {
    const saved = sessionGet(SESSION_KEYS.auth);

    if (!saved) {
      return defaultUserPersistent();
    }

    if (typeof saved === 'string') {
      try {
        return JSON.parse(saved);
      } catch {
        return defaultUserPersistent();
      }
    }

    return saved;
  },

  clearUserPersistent() {
    sessionRemove(SESSION_KEYS.refresh);
    sessionRemove(SESSION_KEYS.access);
    sessionRemove(SESSION_KEYS.auth);

    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
    delete (axios.defaults.headers as any)?.common?.Authorization;
  },

  getUserBalance() {
    const bal = sessionGet<number>(SESSION_KEYS.balance);
    if (!bal) return 0;
    return bal;
  },

  currencyFormatter(balance: number) {
    const x = balance;

    if (x === 0) {
      return '0.00';
    }

    if (!x) {
      return x as any;
    }

    return x.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');
  },

  balanceFormatter(balance: number) {
    if (balance === 0) {
      return '0.00';
    }

    if (balance === null || balance === undefined) {
      return balance as any;
    }

    const splitBalance = balance.toString().split('.');
    const lengthBalance = splitBalance[1] ? splitBalance[1].length : 0;

    let formattedBalance: string;

    if (lengthBalance >= 3) {
      formattedBalance = balance.toFixed(3);
    } else if (lengthBalance === 1) {
      formattedBalance = balance.toFixed(2);
    } else {
      formattedBalance = balance.toFixed(2);
    }

    return formattedBalance.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  },

  colorCurrency(value: number) {
    if (value < 0) return 'text-red-500';
    return '';
  },

  colorCurrencyLog(value: number) {
    if (value > 0) return 'text-primary';
    if (value < 0) return 'text-red-500';
    return '';
  },

  colorCurrencyFoot(val: number) {
    if (val < 0) return 'red-text-foot';
    return '';
  },

  colorWinloss(value: number) {
    if (value > 0) return 'text-primary';
    if (value < 0) return 'text-red-500';
    return 'blue-text';
  },

  winloseStatus(value: number) {
    if (value > 0) return 'Win';
    if (value < 0) return 'Loss';
    return 'Tie';
  },

  getRefreshToken() {
    return sessionGet<string>(SESSION_KEYS.refresh);
  },

  encryptAES(subPassword: string) {
    return utils.encrypt(subPassword);
  },

  encryptAESHeader() {
    const text = 'b34f63f8733328b5c6c9a98354f70137';
    return utils.encrypt(text);
  },

  encryptSensitiveData(request: Record<string, any>) {
    let cloned = { ...request };

    Object.keys(cloned).forEach(dataKey => {
      if (SENSITIVE_KEY.includes(dataKey as any)) {
        cloned = Object.assign(cloned, {
          [dataKey]: utils.encrypt(cloned[dataKey])
        });
      }
    });

    return cloned;
  },

  encrypt(data: any) {
    return rsaKey.encrypt(data, 'base64');
  },

  install(app: import('vue').App) {
    app.config.globalProperties.dateTimeFormatter = dateTimeFormatter;
    app.config.globalProperties.commaFormatter = commaFormatter;
    app.config.globalProperties.upperCamel = upperCamel;
  },

  _session: {
    get: sessionGet,
    set: sessionSet,
    remove: sessionRemove
  }
};

export default utils;
